import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, MenuItem, FormControl, InputLabel, Select, TextField, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Paper, Divider } from '@mui/material';
import Editor from '@monaco-editor/react';
import { html as beautifyHtml } from 'js-beautify';
import Handlebars from 'handlebars';
import { registerDefaultEmailHelpers } from '../../services/handlebarsHelpers';
import { listEmailTemplates, updateEmailTemplate, addEmailTemplate, addEmailTemplateSet, renameEmailTemplateSet, deleteEmailTemplate, listEmailBoilerplates, addEmailBoilerplate, updateEmailBoilerplate, deleteEmailBoilerplate, getTemplateOrganizers, getTemplateEventTags, EMAIL_TEMPLATE_LOCALES, EmailTemplate, EmailBoilerplate, TemplateStatus } from '../../services/templateService';
import { enqueueRawEmail } from '../../services/emailService';
import { getDebugLogs, clearDebugLogs } from '../../utils/debugLogger';
import { generateBoilerplateHandlebar, resolveBoilerplates, hasBoilerplateHandlebars, clearBoilerplateCache } from '../../services/boilerplateResolver';

const TemplatesPanel: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<EmailTemplate | null>(null);
  const [selectedLocale, setSelectedLocale] = useState('');
  const [selectedType, setSelectedType] = useState('');
  
  // Filtering state
  const [filterStatus, setFilterStatus] = useState<TemplateStatus | ''>('');
  const [filterOrganizer, setFilterOrganizer] = useState('');
  const [filterEventTag, setFilterEventTag] = useState('');
  const [availableOrganizers, setAvailableOrganizers] = useState<string[]>([]);
  const [availableEventTags, setAvailableEventTags] = useState<string[]>([]);
  
  // Metadata editing state
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [editStatus, setEditStatus] = useState<TemplateStatus>('active');
  const [editOrganizer, setEditOrganizer] = useState('KrUltra');
  const [editEventTags, setEditEventTags] = useState<string[]>([]);
  const [newEventTag, setNewEventTag] = useState('');
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  const locales = EMAIL_TEMPLATE_LOCALES;
  // const types = useMemo(() => templates.filter(t => t.locale === selectedLocale).map(t => t.type), [templates, selectedLocale]);
  
  // Filtered templates based on current filters
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      if (filterStatus && template.status !== filterStatus) return false;
      if (filterOrganizer && template.organizer !== filterOrganizer) return false;
      if (filterEventTag && (!template.eventTags || !template.eventTags.includes(filterEventTag))) return false;
      return true;
    });
  }, [templates, filterStatus, filterOrganizer, filterEventTag]);
  
  const filteredTypes = useMemo(() => {
    return filteredTemplates.filter(t => t.locale === selectedLocale).map(t => t.type);
  }, [filteredTemplates, selectedLocale]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Live preview state
  const [livePreviewSubject, setLivePreviewSubject] = useState('');
  const [livePreviewHtml, setLivePreviewHtml] = useState('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  
  // Debug logs viewer
  const [debugLogsOpen, setDebugLogsOpen] = useState(false);
  const [debugLogs, setDebugLogs] = useState<any[]>([]);
  
  // Monaco editor ref for cursor position
  const editorRef = useRef<any>(null);
  
  // Boilerplate functionality
  const [selectedBoilerplate, setSelectedBoilerplate] = useState('');
  const [selectedVariable, setSelectedVariable] = useState('');
  
  // Boilerplate management
  const [boilerplateDialogOpen, setBoilerplateDialogOpen] = useState(false);
  const [boilerplates, setBoilerplates] = useState<EmailBoilerplate[]>([]);
  const [editingBoilerplate, setEditingBoilerplate] = useState<EmailBoilerplate | null>(null);
  
  // Boilerplate insert mode dialog
  const [insertModeDialogOpen, setInsertModeDialogOpen] = useState(false);
  // new template dialog state
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newType, setNewType] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamedType, setRenamedType] = useState('');
  const [operationError, setOperationError] = useState('');
  // test send dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');

  useEffect(() => {
    // ensure handlebars helpers are registered once
    registerDefaultEmailHelpers(Handlebars);
    (async () => {
      setLoading(true);
      const data = await listEmailTemplates();
      setTemplates(data);
      
      // Load available organizers and event tags for filtering
      try {
        const [organizers, eventTags] = await Promise.all([
          getTemplateOrganizers(),
          getTemplateEventTags()
        ]);
        setAvailableOrganizers(organizers);
        setAvailableEventTags(eventTags);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
      
      setLoading(false);
    })();
    
    // Load boilerplates from Firestore
    loadBoilerplates();
  }, []);

  // Load boilerplates from Firestore
  const loadBoilerplates = async () => {
    try {
      const data = await listEmailBoilerplates();
      console.log('Loaded boilerplates from Firestore:', data.length, 'items');
      
      // If no boilerplates exist, seed with defaults
      if (data.length === 0) {
        console.log('No boilerplates found, seeding defaults...');
        await seedDefaultBoilerplates();
        const newData = await listEmailBoilerplates();
        console.log('After seeding, loaded:', newData.length, 'items');
        setBoilerplates(newData);
      } else {
        console.log('Boilerplates found:', data.map(b => b.tag));
        setBoilerplates(data);
      }
    } catch (error) {
      console.error('Failed to load boilerplates:', error);
      // Set default boilerplates if Firestore fails
      const defaultBoilerplates: EmailBoilerplate[] = [
        {
          id: 'krultra-header',
          tag: 'krultra-header',
          description: 'KrUltra Header with Stationary Image',
          template: '<div style="text-align: center; margin-bottom: 20px; max-width: 600px; margin-left: auto; margin-right: auto;">\n  <img src="{{krultraStationaryImageUrl}}" alt="KrUltra Event" style="max-width: 100%; height: auto; border: 0; display: block;">\n</div>',
          updatedAt: new Date()
        },
        {
          id: 'event-details',
          tag: 'event-details',
          description: 'Event Details Section',
          template: '<div style="margin: 20px 0; padding: 15px; background-color: #f5f5f5; border-radius: 5px;">\n  <h3>Event Details</h3>\n  <p><strong>Event:</strong> {{eventName}} {{eventEdition}}</p>\n  <p><strong>Date:</strong> {{eventDate}}</p>\n  <p><strong>Registration #:</strong> {{registrationNumber}}</p>\n</div>',
          updatedAt: new Date()
        },
        {
          id: 'payment-summary',
          tag: 'payment-summary',
          description: 'Payment Summary Section',
          template: '<div style="margin: 20px 0; padding: 15px; background-color: #e8f5e9; border-radius: 5px;">\n  <h3>Payment Summary</h3>\n  <p><strong>Required:</strong> {{paymentRequired}}</p>\n  <p><strong>Paid:</strong> {{paymentPaid}}</p>\n  <p><strong>Outstanding:</strong> {{paymentOutstanding}}</p>\n</div>',
          updatedAt: new Date()
        },
        {
          id: 'footer',
          tag: 'footer',
          description: 'Email Footer',
          template: '<div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eaeaea; font-size: 12px; color: #666; text-align: center; max-width: 600px; margin-left: auto; margin-right: auto;">\n  <p>This email was sent to {{email}}. If you did not register for this event, please ignore this email.</p>\n  <p>Best regards,<br>KrUltra Team</p>\n</div>',
          updatedAt: new Date()
        },
        {
          id: 'heading-h1',
          tag: 'heading-h1',
          description: 'H1 Heading',
          template: '<h1 style="color: #2e7d32; margin-bottom: 16px;">Your Heading Here</h1>',
          updatedAt: new Date()
        },
        {
          id: 'heading-h2',
          tag: 'heading-h2',
          description: 'H2 Heading',
          template: '<h2 style="color: #2e7d32; margin-bottom: 12px;">Your Heading Here</h2>',
          updatedAt: new Date()
        },
        {
          id: 'heading-h3',
          tag: 'heading-h3',
          description: 'H3 Heading',
          template: '<h3 style="color: #2e7d32; margin-bottom: 8px;">Your Heading Here</h3>',
          updatedAt: new Date()
        },
        {
          id: 'paragraph',
          tag: 'paragraph',
          description: 'Paragraph Text',
          template: '<p>Your paragraph text here.</p>',
          updatedAt: new Date()
        },
        {
          id: 'button-primary',
          tag: 'button-primary',
          description: 'Primary Button',
          template: '<div style="text-align: center; margin: 20px 0;">\n  <a href="{{buttonUrl}}" style="display: inline-block; background-color: #1976d2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;">{{buttonText}}</a>\n</div>',
          updatedAt: new Date()
        },
        {
          id: 'button-secondary',
          tag: 'button-secondary',
          description: 'Secondary Button',
          template: '<div style="text-align: center; margin: 20px 0;">\n  <a href="{{buttonUrl}}" style="display: inline-block; background-color: #6c757d; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;">{{buttonText}}</a>\n</div>',
          updatedAt: new Date()
        },
        {
          id: 'alert-info',
          tag: 'alert-info',
          description: 'Info Alert',
          template: '<div style="background-color: #d1ecf1; border: 1px solid #17a2b8; padding: 12px; border-radius: 4px; margin: 16px 0;">\n  <strong>Info:</strong> Your info message here.\n</div>',
          updatedAt: new Date()
        },
        {
          id: 'alert-warning',
          tag: 'alert-warning',
          description: 'Warning Alert',
          template: '<div style="background-color: #fff3cd; border: 1px solid #ffc107; padding: 12px; border-radius: 4px; margin: 16px 0;">\n  <strong>Warning:</strong> Your warning message here.\n</div>',
          updatedAt: new Date()
        },
        {
          id: 'alert-error',
          tag: 'alert-error',
          description: 'Error Alert',
          template: '<div style="background-color: #f8d7da; border: 1px solid #f44336; padding: 12px; border-radius: 4px; margin: 16px 0;">\n  <strong>Error:</strong> Your error message here.\n</div>',
          updatedAt: new Date()
        }
      ];
      setBoilerplates(defaultBoilerplates);
    }
  };

  // Seed default boilerplates into Firestore
  const seedDefaultBoilerplates = async () => {
    const defaultBoilerplates: EmailBoilerplate[] = [
      {
        id: 'krultra-header',
        tag: 'krultra-header',
        description: 'KrUltra Header with Stationary Image',
        template: '<div style="text-align: center; margin-bottom: 20px; max-width: 600px; margin-left: auto; margin-right: auto;">\n  <img src="{{krultraStationaryImageUrl}}" alt="KrUltra Event" style="max-width: 100%; height: auto; border: 0; display: block;">\n</div>',
        updatedAt: new Date()
      },
      {
        id: 'event-details',
        tag: 'event-details',
        description: 'Event Details Section',
        template: '<div style="margin: 20px 0; padding: 15px; background-color: #f5f5f5; border-radius: 5px;">\n  <h3>Event Details</h3>\n  <p><strong>Event:</strong> {{eventName}} {{eventEdition}}</p>\n  <p><strong>Date:</strong> {{eventDate}}</p>\n  <p><strong>Registration #:</strong> {{registrationNumber}}</p>\n</div>',
        updatedAt: new Date()
      },
      {
        id: 'payment-summary',
        tag: 'payment-summary',
        description: 'Payment Summary Section',
        template: '<div style="margin: 20px 0; padding: 15px; background-color: #e8f5e9; border-radius: 5px;">\n  <h3>Payment Summary</h3>\n  <p><strong>Required:</strong> {{paymentRequired}}</p>\n  <p><strong>Paid:</strong> {{paymentPaid}}</p>\n  <p><strong>Outstanding:</strong> {{paymentOutstanding}}</p>\n</div>',
        updatedAt: new Date()
      },
      {
        id: 'footer',
        tag: 'footer',
        description: 'Email Footer',
        template: '<div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eaeaea; font-size: 12px; color: #666; text-align: center; max-width: 600px; margin-left: auto; margin-right: auto;">\n  <p>This email was sent to {{email}}. If you did not register for this event, please ignore this email.</p>\n  <p>Best regards,<br>KrUltra Team</p>\n</div>',
        updatedAt: new Date()
      },
      {
        id: 'heading-h1',
        tag: 'heading-h1',
        description: 'H1 Heading',
        template: '<h1 style="color: #2e7d32; margin-bottom: 16px;">Your Heading Here</h1>',
        updatedAt: new Date()
      },
      {
        id: 'heading-h2',
        tag: 'heading-h2',
        description: 'H2 Heading',
        template: '<h2 style="color: #2e7d32; margin-bottom: 12px;">Your Heading Here</h2>',
        updatedAt: new Date()
      },
      {
        id: 'heading-h3',
        tag: 'heading-h3',
        description: 'H3 Heading',
        template: '<h3 style="color: #2e7d32; margin-bottom: 8px;">Your Heading Here</h3>',
        updatedAt: new Date()
      },
      {
        id: 'paragraph',
        tag: 'paragraph',
        description: 'Paragraph Text',
        template: '<p>Your paragraph text here.</p>',
        updatedAt: new Date()
      },
      {
        id: 'button-primary',
        tag: 'button-primary',
        description: 'Primary Button',
        template: '<div style="text-align: center; margin: 20px 0;">\n  <a href="{{buttonUrl}}" style="display: inline-block; background-color: #1976d2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;">{{buttonText}}</a>\n</div>',
        updatedAt: new Date()
      },
      {
        id: 'button-secondary',
        tag: 'button-secondary',
        description: 'Secondary Button',
        template: '<div style="text-align: center; margin: 20px 0;">\n  <a href="{{buttonUrl}}" style="display: inline-block; background-color: #6c757d; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;">{{buttonText}}</a>\n</div>',
        updatedAt: new Date()
      },
      {
        id: 'alert-info',
        tag: 'alert-info',
        description: 'Info Alert',
        template: '<div style="background-color: #d1ecf1; border: 1px solid #17a2b8; padding: 12px; border-radius: 4px; margin: 16px 0;">\n  <strong>Info:</strong> Your info message here.\n</div>',
        updatedAt: new Date()
      },
      {
        id: 'alert-warning',
        tag: 'alert-warning',
        description: 'Warning Alert',
        template: '<div style="background-color: #fff3cd; border: 1px solid #ffc107; padding: 12px; border-radius: 4px; margin: 16px 0;">\n  <strong>Warning:</strong> Your warning message here.\n</div>',
        updatedAt: new Date()
      },
      {
        id: 'alert-error',
        tag: 'alert-error',
        description: 'Error Alert',
        template: '<div style="background-color: #f8d7da; border: 1px solid #f44336; padding: 12px; border-radius: 4px; margin: 16px 0;">\n  <strong>Error:</strong> Your error message here.\n</div>',
        updatedAt: new Date()
      }
    ];

    try {
      for (const boilerplate of defaultBoilerplates) {
        await addEmailBoilerplate(boilerplate.tag, boilerplate.description, boilerplate.template);
      }
      console.log('Default boilerplates seeded successfully');
    } catch (error) {
      console.error('Failed to seed default boilerplates:', error);
    }
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (current) setCurrent({ ...current, subjectTemplate: e.target.value });
  };

  const handleBodyChange = (value: string | undefined) => {
    if (current && value !== undefined) setCurrent({ ...current, bodyTemplate: value });
  };

  const handleFormat = () => {
    if (current) {
      const formatted = beautifyHtml(current.bodyTemplate, { indent_size: 2 });
      setCurrent({ ...current, bodyTemplate: formatted });
    }
  };

  // Handle editor mount to store ref
  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  // Update live preview when template changes
  const updateLivePreview = useCallback(async () => {
    if (!current) {
      setLivePreviewSubject('');
      setLivePreviewHtml('');
      setPreviewError(null);
      return;
    }
    try {
      const previewAppBaseUrl =
        process.env.REACT_APP_RUNNERSHUB_URL ||
        process.env.REACT_APP_BASE_URL ||
        process.env.APP_BASE_URL ||
        process.env.REACT_APP_PUBLIC_URL ||
        process.env.PUBLIC_URL ||
        'https://runnershubtest.web.app';

      // Use the same context as the full preview for consistency
      const context: any = {
        eventName: 'KrUltra TestEvent',
        eventShortName: 'KUTE',
        eventEdition: new Date().getFullYear(),
        appBaseUrl: String(previewAppBaseUrl).replace(/\/$/, ''),
        eventDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
        representing: 'KrUltra Runners',
        name: 'Test User',
        firstName: 'Test',
        lastName: 'User',
        email: 'test@krultra.com',
        personId: 'abc123def456',
        registrationNumber: 12345,
        dateOfBirth: new Date('1990-01-01'),
        nationality: 'NOR',
        phoneCountryCode: '+47',
        phoneNumber: '123456789',
        travelRequired: 'Yes',
        termsAccepted: true,
        comments: 'No comments',
        notifyFutureEvents: true,
        sendRunningOffers: false,
        waitinglistExpires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        locale: current.locale || 'en',
        languagePreference: current.locale || 'en',
        languagePreferenceVerbose: current.locale === 'no' ? 'Norsk' : 'English',
        status: current.locale === 'no' ? 'Ubekreftet' : 'Pending',
        paymentAmount: '500',
        paymentPaid: '200',
        paymentRequired: '500',
        paymentStatus: current.locale === 'no' ? 'Delvis betaling' : 'Partial payment',
        paymentOutstanding: '300',
        // Add stationary image URL for preview
        krultraStationaryImageUrl: process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL || 'https://krultra.no/assets/email/krultra_stationary_top.png',
        headerImageUrl: process.env.REACT_APP_KRULTRA_HEADER_IMAGE_URL || process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL || 'https://krultra.no/assets/email/krultra_stationary_top.png',
        logoUrl: process.env.REACT_APP_KRULTRA_LOGO_URL || process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL || 'https://krultra.no/assets/email/krultra_stationary_top.png',
        unsubscribeUrl: 'https://europe-west1-runnershubtest.cloudfunctions.net/unsubscribe?token=preview-dummy-token&type=notifyFutureEvents',
        dateYearCurrent: new Date().getFullYear().toString(),
        dateYearPrevious: (new Date().getFullYear() - 1).toString(),
        dateYearNext: (new Date().getFullYear() + 1).toString(),
        dateToday: new Date().toLocaleDateString((current.locale === 'no' || current.locale === 'nb-NO') ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
        dateEventEditionStartTime: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toLocaleDateString((current.locale === 'no' || current.locale === 'nb-NO') ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
        datetimeEventEditionRegistrationOpens: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleString((current.locale === 'no' || current.locale === 'nb-NO') ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
        datetimeEventEditionRegistrationDeadline: new Date(Date.now() + 53 * 24 * 60 * 60 * 1000).toLocaleString((current.locale === 'no' || current.locale === 'nb-NO') ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      };
      
      // Format dates for preview as '1 Jan 1990' (same as full preview)
      const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      context.dateOfBirth = fmt(context.dateOfBirth);
      context.waitinglistExpires = fmt(context.waitinglistExpires);
      context.today = fmt(new Date());
      
      // Resolve boilerplate handlebars before Handlebars compilation
      let resolvedSubject = current.subjectTemplate || '';
      let resolvedBody = current.bodyTemplate || '';
      
      if (hasBoilerplateHandlebars(resolvedSubject)) {
        const subjectResult = await resolveBoilerplates(resolvedSubject, {
          locale: current.locale || 'en',
          onMissingBoilerplate: 'warn'
        });
        resolvedSubject = subjectResult.resolvedContent;
        if (subjectResult.warnings.length > 0) {
          console.log('[Preview] Boilerplate warnings in subject:', subjectResult.warnings);
        }
      }
      
      if (hasBoilerplateHandlebars(resolvedBody)) {
        const bodyResult = await resolveBoilerplates(resolvedBody, {
          locale: current.locale || 'en',
          onMissingBoilerplate: 'warn'
        });
        resolvedBody = bodyResult.resolvedContent;
        if (bodyResult.warnings.length > 0) {
          console.log('[Preview] Boilerplate warnings in body:', bodyResult.warnings);
        }
      }
      
      const subj = resolvedSubject 
        ? Handlebars.compile(resolvedSubject)(context) 
        : '';
      const html = resolvedBody 
        ? Handlebars.compile(resolvedBody)(context) 
        : '';
      
      setLivePreviewSubject(subj);
      setLivePreviewHtml(html);
      setPreviewError(null);
    } catch (e: any) {
      setPreviewError(e?.message || 'Preview error');
    }
  }, [current]);

  // Debounced live preview update
  useEffect(() => {
    const timer = setTimeout(updateLivePreview, 300);
    return () => clearTimeout(timer);
  }, [current?.subjectTemplate, current?.bodyTemplate, updateLivePreview]);

  // Boilerplate handlers
  const handleBoilerplateInsertClick = () => {
    if (!current || !selectedBoilerplate) return;
    // Open dialog to choose insert mode
    setInsertModeDialogOpen(true);
  };
  
  // Insert boilerplate as handlebar reference ({{>boilerplateName}})
  const handleInsertAsHandlebar = () => {
    if (!current || !selectedBoilerplate) return;
    
    const boilerplate = boilerplates.find(b => b.id === selectedBoilerplate);
    if (!boilerplate) return;
    
    const handlebarText = generateBoilerplateHandlebar(boilerplate.tag);
    
    if (editorRef.current) {
      const editor = editorRef.current;
      const position = editor.getPosition();
      if (position) {
        editor.executeEdits('', [{
          range: {
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column
          },
          text: handlebarText
        }]);
        editor.focus();
      }
    }
    setInsertModeDialogOpen(false);
  };
  
  // Insert boilerplate content directly (current behavior)
  const handleInsertAsContent = () => {
    if (!current || !selectedBoilerplate) return;
    
    const boilerplate = boilerplates.find(b => b.id === selectedBoilerplate);
    if (!boilerplate) return;
    
    if (editorRef.current) {
      const editor = editorRef.current;
      const position = editor.getPosition();
      if (position) {
        editor.executeEdits('', [{
          range: {
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column
          },
          text: boilerplate.template
        }]);
        editor.focus();
      }
    }
    setInsertModeDialogOpen(false);
  };

  // Boilerplate management handlers
  const handleAddBoilerplate = () => {
    setEditingBoilerplate({ 
      id: '', 
      tag: '', 
      description: '', 
      template: '', 
      updatedAt: new Date() 
    });
    setBoilerplateDialogOpen(true);
  };

  const handleEditBoilerplate = (id: string) => {
    const boilerplate = boilerplates.find(b => b.id === id);
    if (boilerplate) {
      setEditingBoilerplate(boilerplate);
      setBoilerplateDialogOpen(true);
    }
  };

  const handleSaveBoilerplate = async () => {
    if (!editingBoilerplate) return;
    
    try {
      if (editingBoilerplate.id) {
        // Update existing boilerplate
        await updateEmailBoilerplate(
          editingBoilerplate.id,
          editingBoilerplate.tag,
          editingBoilerplate.description,
          editingBoilerplate.template
        );
      } else {
        // Add new boilerplate
        await addEmailBoilerplate(
          editingBoilerplate.tag,
          editingBoilerplate.description,
          editingBoilerplate.template
        );
      }
      
      // Clear boilerplate cache so templates using this boilerplate get updated content
      clearBoilerplateCache();
      
      // Reload boilerplates
      await loadBoilerplates();
      setBoilerplateDialogOpen(false);
      setEditingBoilerplate(null);
      
      // Refresh preview if a template is selected
      if (current) {
        updateLivePreview();
      }
    } catch (error) {
      console.error('Failed to save boilerplate:', error);
    }
  };

  const handleDeleteBoilerplate = async (id: string) => {
    if (window.confirm(`Delete boilerplate "${id}"?`)) {
      try {
        await deleteEmailBoilerplate(id);
        
        // Clear boilerplate cache
        clearBoilerplateCache();
        
        await loadBoilerplates();
        if (selectedBoilerplate === id) {
          setSelectedBoilerplate('');
        }
        
        // Refresh preview if a template is selected
        if (current) {
          updateLivePreview();
        }
      } catch (error) {
        console.error('Failed to delete boilerplate:', error);
      }
    }
  };

  const handleResetBoilerplates = async () => {
    if (window.confirm('Reset all boilerplates to defaults? This will delete all existing boilerplates and restore the original set.')) {
      try {
        // First, delete all existing boilerplates
        const currentBoilerplates = await listEmailBoilerplates();
        for (const boilerplate of currentBoilerplates) {
          await deleteEmailBoilerplate(boilerplate.id);
        }
        
        // Then seed with defaults
        await seedDefaultBoilerplates();
        
        // Clear boilerplate cache
        clearBoilerplateCache();
        
        await loadBoilerplates();
        console.log('Boilerplates reset successfully');
        
        // Refresh preview if a template is selected
        if (current) {
          updateLivePreview();
        }
      } catch (error) {
        console.error('Failed to reset boilerplates:', error);
      }
    }
  };

//  const handleVariableInsert = () => {
//    if (!current || !selectedVariable) return;
    
//    const insertText = `{{${selectedVariable}}}`;
//    if (editorRef.current) {
//      const editor = editorRef.current;
//      const position = editor.getPosition();
//      if (position) {
//        editor.executeEdits('', [{
//          range: {
//            startLineNumber: position.lineNumber,
//            startColumn: position.column,
//            endLineNumber: position.lineNumber,
//            endColumn: position.column
//          },
//          text: insertText
//        }]);
//        editor.focus();
//      }
//    }
//  };

  
  const buildPreviewFromCurrent = async () => {
    if (!current) return { subject: '', html: '', context: {} };
    const locale = current.locale || 'en';
    const isNorwegian = locale === 'no' || locale === 'nb-NO' || locale === 'nb';
    const appBaseUrl =
      process.env.REACT_APP_RUNNERSHUB_URL ||
      process.env.REACT_APP_BASE_URL ||
      process.env.APP_BASE_URL ||
      process.env.REACT_APP_PUBLIC_URL ||
      process.env.PUBLIC_URL ||
      (typeof window !== 'undefined' && window.location?.origin) ||
      'https://krultra.no';
    const context: any = {
      eventName: 'KrUltra TestEvent',
      eventShortName: 'KUTE',
      eventEdition: new Date().getFullYear(),
      appBaseUrl: String(appBaseUrl).replace(/\/$/, ''),
      eventDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
      representing: 'KrUltra Runners',
      name: 'Test User',
      firstName: 'Test',
      lastName: 'User',
      email: 'test@krultra.com',
      registrationNumber: 12345,
      dateOfBirth: new Date('1990-01-01'),
      nationality: 'NOR',
      phoneCountryCode: '+47',
      phoneNumber: '123456789',
      travelRequired: 'Yes',
      termsAccepted: true,
      comments: 'No comments',
      notifyFutureEvents: true,
      sendRunningOffers: false,
      waitinglistExpires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      locale: locale,
      // Language preference fields (localized)
      languagePreference: locale,
      languagePreferenceVerbose: isNorwegian ? 'Norsk' : 'English',
      preferredLanguage: isNorwegian ? 'Norsk' : 'English',
      // Status field (localized)
      status: isNorwegian ? 'Ubekreftet' : 'Pending',
      // Race distance (localized example)
      raceDistance: isNorwegian ? '55 km Ultra' : '55 km Ultra',
      // Payment fields (with aliases for template compatibility)
      paymentAmount: 1500,
      paymentPaid: 500,
      paymentRequired: 1500,
      paymentMade: 500,
      paymentOutstanding: 1000,
      paymentStatus: 'partial',
      // License fields
      hasYearLicense: true,
      licenseNumber: '123456-2026',
      // Add stationary image URL for test emails
      krultraStationaryImageUrl: process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL || 'https://krultra.no/assets/email/krultra_stationary_top.png',
      headerImageUrl: process.env.REACT_APP_KRULTRA_HEADER_IMAGE_URL || process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL || 'https://krultra.no/assets/email/krultra_stationary_top.png',
      logoUrl: process.env.REACT_APP_KRULTRA_LOGO_URL || process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL || 'https://krultra.no/assets/email/krultra_stationary_top.png',
      unsubscribeUrl: 'https://europe-west1-runnershubtest.cloudfunctions.net/unsubscribe?token=preview-dummy-token&type=notifyFutureEvents',
      dateYearCurrent: new Date().getFullYear().toString(),
      dateYearPrevious: (new Date().getFullYear() - 1).toString(),
      dateYearNext: (new Date().getFullYear() + 1).toString(),
      dateToday: new Date().toLocaleDateString(isNorwegian ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      dateEventEditionStartTime: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toLocaleDateString(isNorwegian ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      datetimeEventEditionRegistrationOpens: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleString(isNorwegian ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      datetimeEventEditionRegistrationDeadline: new Date(Date.now() + 53 * 24 * 60 * 60 * 1000).toLocaleString(isNorwegian ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    };
    const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    context.dateOfBirth = fmt(context.dateOfBirth);
    context.waitinglistExpires = fmt(context.waitinglistExpires);
    context.today = fmt(new Date());
    
    // Resolve boilerplates before compiling
    const subjectTemplate = current.subjectTemplate || '';
    const bodyTemplate = current.bodyTemplate || '';
    let resolvedSubject = subjectTemplate;
    let resolvedBody = bodyTemplate;
    
    if (hasBoilerplateHandlebars(subjectTemplate)) {
      const subjectResult = await resolveBoilerplates(subjectTemplate, {
        locale: locale,
        onMissingBoilerplate: 'warn'
      });
      resolvedSubject = subjectResult.resolvedContent;
    }
    
    if (hasBoilerplateHandlebars(bodyTemplate)) {
      const bodyResult = await resolveBoilerplates(bodyTemplate, {
        locale: locale,
        onMissingBoilerplate: 'warn'
      });
      resolvedBody = bodyResult.resolvedContent;
    }
    
    const subj = Handlebars.compile(resolvedSubject)(context);
    const html = Handlebars.compile(resolvedBody)(context);
    return { subject: subj, html, context };
  };

  const handleTestSend = async () => {
    if (!current || !testRecipient) return;
    try {
      const { subject, html, context } = await buildPreviewFromCurrent();
      await enqueueRawEmail(testRecipient, subject, html, { type: current.type, context });
      setTestDialogOpen(false);
      setTestRecipient('');
      alert('Test email enqueued.');
    } catch (e: any) {
      alert('Failed to enqueue test email: ' + (e?.message || e));
    }
  };

  // Debug logs functions
  const handleShowDebugLogs = () => {
    const logs = getDebugLogs();
    setDebugLogs(logs.reverse()); // Show newest first
    setDebugLogsOpen(true);
  };

  const handleClearDebugLogs = () => {
    clearDebugLogs();
    setDebugLogs([]);
  };

  const handlePreview = async () => {
    if (!current) {
      return;
    }
    try {
      const { subject, html } = await buildPreviewFromCurrent();
      setPreviewSubject(subject);
      setPreviewHtml(html);
      setPreviewOpen(true);
    } catch (e: any) {
      alert('Preview error: ' + (e?.message || e));
    }
  };

  const handleSave = async () => {
    if (current) {
      // Validate boilerplate references before saving
      const contentToValidate = `${current.subjectTemplate || ''} ${current.bodyTemplate || ''}`;
      if (hasBoilerplateHandlebars(contentToValidate)) {
        try {
          const subjectResult = await resolveBoilerplates(current.subjectTemplate || '', {
            locale: current.locale || 'en',
            onMissingBoilerplate: 'warn'
          });
          const bodyResult = await resolveBoilerplates(current.bodyTemplate || '', {
            locale: current.locale || 'en',
            onMissingBoilerplate: 'warn'
          });
          
          const allWarnings = [...subjectResult.warnings, ...bodyResult.warnings];
          const allErrors = [...subjectResult.errors, ...bodyResult.errors];
          
          // Show errors (circular references, etc.)
          if (allErrors.length > 0) {
            alert(`Template has boilerplate errors:\n${allErrors.join('\n')}\n\nPlease fix these before saving.`);
            return;
          }
          
          // Show warnings (missing boilerplates) but allow saving
          if (allWarnings.length > 0) {
            const proceed = window.confirm(
              `Template has boilerplate warnings:\n${allWarnings.join('\n')}\n\nMissing boilerplates will be rendered as literal text. Continue saving?`
            );
            if (!proceed) return;
          }
        } catch (e: any) {
          console.error('Error validating boilerplates:', e);
          // Continue with save even if validation fails
        }
      }
      
      await updateEmailTemplate(
        current.type,
        current.locale,
        current.subjectTemplate,
        current.bodyTemplate,
        { status: current.status, organizer: current.organizer, eventTags: current.eventTags }
      );
      alert('Template saved');
      const data = await listEmailTemplates();
      setTemplates(data);
    }
  };

  // const handleOpenMetadataDialog = () => {
  //  if (current) {
  //    setEditStatus(current.status || 'active');
  //    setEditOrganizer(current.organizer || 'KrUltra');
  //    setEditEventTags(current.eventTags || []);
  //    setMetadataDialogOpen(true);
  //  }
  //};

  const handleSaveMetadata = async () => {
    if (current) {
      await updateEmailTemplate(current.type, current.locale, current.subjectTemplate, current.bodyTemplate, {
        status: editStatus,
        organizer: editOrganizer,
        eventTags: editEventTags
      });
      
      // Update current template with new metadata
      setCurrent({
        ...current,
        status: editStatus,
        organizer: editOrganizer,
        eventTags: editEventTags
      });
      
      // Reload templates to update filter options
      const data = await listEmailTemplates();
      setTemplates(data);
      
      // Reload filter options
      try {
        const [organizers, eventTags] = await Promise.all([
          getTemplateOrganizers(),
          getTemplateEventTags()
        ]);
        setAvailableOrganizers(organizers);
        setAvailableEventTags(eventTags);
      } catch (error) {
        console.error('Failed to reload filter options:', error);
      }
      
      setMetadataDialogOpen(false);
      alert('Metadata saved');
    }
  };

  const handleAddEventTag = () => {
    if (newEventTag.trim() && !editEventTags.includes(newEventTag.trim())) {
      setEditEventTags([...editEventTags, newEventTag.trim()]);
      setNewEventTag('');
    }
  };

  const handleRemoveEventTag = (tagToRemove: string) => {
    setEditEventTags(editEventTags.filter(tag => tag !== tagToRemove));
  };


  const handleExport = async () => {
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: `emailTemplates_${new Date().toISOString()}.json`,
          types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(JSON.stringify(templates, null, 2));
        await writable.close();
      } catch {
        alert('Save cancelled or failed');
      }
    } else if ('chooseFileSystemEntries' in window) {
      try {
        const opts = { type: 'save-file', accepts: [{ description: 'JSON', extensions: ['json'], mimeTypes: ['application/json'] }] };
        const fileHandle = await (window as any).chooseFileSystemEntries(opts);
        const writable = await fileHandle.createWritable();
        await writable.write(JSON.stringify(templates, null, 2));
        await writable.close();
      } catch {
        alert('Save cancelled or failed');
      }
    } else {
      const dataStr = JSON.stringify(templates, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emailTemplates_${new Date().toISOString()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const text = await file.text();
      try {
        const importedTemplates: EmailTemplate[] = JSON.parse(text);
        const template = importedTemplates[0];
        
        await addEmailTemplate(template.type, template.locale, template.subjectTemplate, template.bodyTemplate, {
          status: template.status,
          organizer: template.organizer,
          eventTags: template.eventTags
        });
        
        const data = await listEmailTemplates();
        setTemplates(data);
        
        // Reload filter options
        try {
          const [organizers, eventTags] = await Promise.all([
            getTemplateOrganizers(),
            getTemplateEventTags()
          ]);
          setAvailableOrganizers(organizers);
          setAvailableEventTags(eventTags);
        } catch (error) {
          console.error('Failed to reload filter options:', error);
        }
        
        alert('Template imported successfully');
      } catch (err) {
        alert('Import failed: ' + err);
      }
    }
  };

  const triggerImport = () => fileInputRef.current?.click();

  const addNewTemplate = async () => {
    const normalizedType = newType.trim();
    if (!normalizedType) return;

    setLoading(true);
    setOperationError('');
    try {
      await addEmailTemplateSet(normalizedType);
      const data = await listEmailTemplates();
      setTemplates(data);
      setSelectedLocale('no');
      setSelectedType(normalizedType);
      setCurrent(data.find(template => template.locale === 'no' && template.type === normalizedType) || null);
      setNewType('');
      setNewDialogOpen(false);
    } catch (error: any) {
      setOperationError(error?.message || String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async () => {
    if (!current) return;
    const normalizedType = renamedType.trim();
    if (!normalizedType) return;

    setLoading(true);
    setOperationError('');
    try {
      await renameEmailTemplateSet(current.type, normalizedType);
      const data = await listEmailTemplates();
      setTemplates(data);
      setSelectedType(normalizedType);
      setCurrent(data.find(template =>
        template.locale === current.locale && template.type === normalizedType
      ) || null);
      setRenameDialogOpen(false);
      setRenamedType('');
    } catch (error: any) {
      setOperationError(error?.message || String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!current) return;
    if (!window.confirm('Delete this template?')) return;
    setLoading(true);
    await deleteEmailTemplate(current.id);
    const data = await listEmailTemplates();
    setTemplates(data);
    setCurrent(null); setSelectedType('');
    setLoading(false);
  };

  return (
    <Box>
      <Typography variant="h5">Email Templates</Typography>
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handleExport}>Export</Button>
        <Button variant="outlined" onClick={triggerImport}>Import</Button>
        <input type="file" accept="application/json" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImport} />
        <Button variant="contained" onClick={() => { setOperationError(''); setNewDialogOpen(true); }}>Add Template</Button>
        <Button variant="outlined" color="info" onClick={handleShowDebugLogs}>Debug Logs</Button>
      </Box>
      {loading ? (
        <CircularProgress sx={{ mt: 2 }} />
      ) : (
        <Box>
          {/* Filtering Controls */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={e => { setFilterStatus(e.target.value as TemplateStatus | ''); setSelectedType(''); setCurrent(null); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="deprecated">Deprecated</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Organizer</InputLabel>
              <Select value={filterOrganizer} label="Organizer" onChange={e => { setFilterOrganizer(e.target.value); setSelectedType(''); setCurrent(null); }}>
                <MenuItem value="">All</MenuItem>
                {availableOrganizers.map(org => <MenuItem key={org} value={org}>{org}</MenuItem>)}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Event Tag</InputLabel>
              <Select value={filterEventTag} label="Event Tag" onChange={e => { setFilterEventTag(e.target.value); setSelectedType(''); setCurrent(null); }}>
                <MenuItem value="">All</MenuItem>
                {availableEventTags.map(tag => <MenuItem key={tag} value={tag}>{tag}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Locale then Template Type selection */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Locale</InputLabel>
              <Select value={selectedLocale} label="Locale" onChange={e => { setSelectedLocale(e.target.value); setSelectedType(''); setCurrent(null); }}>
                {locales.map(locale => <MenuItem key={locale.code} value={locale.code}>{locale.label} ({locale.code})</MenuItem>)}
              </Select>
            </FormControl>
            {selectedLocale && (
              <FormControl fullWidth>
                <InputLabel>Template Type</InputLabel>
                <Select value={selectedType} label="Template Type" onChange={e => {
                  const type = e.target.value;
                  setSelectedType(type);
                  const tpl = filteredTemplates.find(t => t.locale === selectedLocale && t.type === type) || null;
                  setCurrent(tpl);
                }}>
                  {filteredTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
          {current && (
            <Box sx={{ mt: 2 }}>
              <TextField label="Subject" fullWidth value={current.subjectTemplate} onChange={handleSubjectChange} />
              
              {/* Metadata Fields - Directly Visible */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={current.status || 'active'}
                    label="Status"
                    onChange={(e) => {
                      const updated = { ...current, status: e.target.value as TemplateStatus };
                      setCurrent(updated);
                    }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  size="small"
                  label="Organizer"
                  value={current.organizer || ''}
                  onChange={(e) => {
                    const updated = { ...current, organizer: e.target.value };
                    setCurrent(updated);
                  }}
                  placeholder="e.g., KrUltra"
                  sx={{ minWidth: 150 }}
                />
                
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>Event Tags</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    {(current.eventTags || []).map((tag) => (
                      <Button
                        key={tag}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          const updated = { 
                            ...current, 
                            eventTags: (current.eventTags || []).filter(t => t !== tag)
                          };
                          setCurrent(updated);
                        }}
                        sx={{ textTransform: 'none', height: 24, fontSize: '0.75rem' }}
                      >
                        {tag} ×
                      </Button>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextField
                      size="small"
                      placeholder="Add tag (e.g., KUTC)"
                      value={newEventTag}
                      onChange={(e) => setNewEventTag(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newEventTag.trim()) {
                          e.preventDefault();
                          if (!current.eventTags?.includes(newEventTag.trim())) {
                            const updated = {
                              ...current,
                              eventTags: [...(current.eventTags || []), newEventTag.trim()]
                            };
                            setCurrent(updated);
                          }
                          setNewEventTag('');
                        }
                      }}
                      sx={{ minWidth: 120 }}
                    />
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => {
                        if (newEventTag.trim() && !current.eventTags?.includes(newEventTag.trim())) {
                          const updated = {
                            ...current,
                            eventTags: [...(current.eventTags || []), newEventTag.trim()]
                          };
                          setCurrent(updated);
                          setNewEventTag('');
                        }
                      }}
                      disabled={!newEventTag.trim() || current.eventTags?.includes(newEventTag.trim())}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
              </Box>
              
              {/* Side-by-side layout: Editor (left) and Preview (right) */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                {/* Editor Panel */}
                <Paper elevation={2} sx={{ flex: 1, minWidth: 300, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Template Editor</Typography>
                  
                  {/* Boilerplate and Variable Dropdowns */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Boilerplate</InputLabel>
                      <Select
                        value={selectedBoilerplate}
                        label="Boilerplate"
                        onChange={(e) => setSelectedBoilerplate(e.target.value)}
                      >
                        {[...boilerplates].sort((a, b) => a.tag.localeCompare(b.tag)).map((boilerplate) => (
                          <MenuItem key={boilerplate.id} value={boilerplate.id}>{boilerplate.tag.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" size="small" onClick={handleBoilerplateInsertClick} disabled={!selectedBoilerplate}>
                      Insert
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleAddBoilerplate}>
                      Add
                    </Button>
                    {selectedBoilerplate && (
                      <Button variant="outlined" size="small" onClick={() => handleEditBoilerplate(selectedBoilerplate)}>
                        Edit
                      </Button>
                    )}
                    {selectedBoilerplate && (
                      <Button variant="outlined" size="small" color="error" onClick={() => handleDeleteBoilerplate(selectedBoilerplate)}>
                        Delete
                      </Button>
                    )}
                    <Button variant="outlined" size="small" color="warning" onClick={handleResetBoilerplates}>
                      Reset All
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Variable</InputLabel>
                      <Select
                        value={selectedVariable}
                        label="Variable"
                        onChange={(e) => {
                          setSelectedVariable(e.target.value);
                          if (e.target.value) {
                            // Insert immediately at cursor position
                            const insertText = `{{${e.target.value}}}`;
                            if (editorRef.current) {
                              const editor = editorRef.current;
                              const position = editor.getPosition();
                              if (position) {
                                editor.executeEdits('', [{
                                  range: {
                                    startLineNumber: position.lineNumber,
                                    startColumn: position.column,
                                    endLineNumber: position.lineNumber,
                                    endColumn: position.column
                                  },
                                  text: insertText
                                }]);
                                editor.focus();
                              }
                            }
                          }
                        }}
                      >
                        <MenuItem value="appBaseUrl">appBaseUrl</MenuItem>
                        <MenuItem value="comments">comments</MenuItem>
                        <MenuItem value="dateEventEditionStartTime">dateEventEditionStartTime</MenuItem>
                        <MenuItem value="dateOfBirth">dateOfBirth</MenuItem>
                        <MenuItem value="datetimeEventEditionRegistrationDeadline">datetimeEventEditionRegistrationDeadline</MenuItem>
                        <MenuItem value="datetimeEventEditionRegistrationOpens">datetimeEventEditionRegistrationOpens</MenuItem>
                        <MenuItem value="dateToday">dateToday</MenuItem>
                        <MenuItem value="dateYearCurrent">dateYearCurrent</MenuItem>
                        <MenuItem value="dateYearNext">dateYearNext</MenuItem>
                        <MenuItem value="dateYearPrevious">dateYearPrevious</MenuItem>
                        <MenuItem value="email">email</MenuItem>
                        <MenuItem value="eventEdition">eventEdition</MenuItem>
                        <MenuItem value="eventName">eventName</MenuItem>
                        <MenuItem value="eventShortName">eventShortName</MenuItem>
                        <MenuItem value="firstName">firstName</MenuItem>
                        <MenuItem value="hasYearLicense">hasYearLicense</MenuItem>
                        <MenuItem value="headerImageUrl">headerImageUrl</MenuItem>
                        <MenuItem value="krultraStationaryImageUrl">krultraStationaryImageUrl</MenuItem>
                        <MenuItem value="languagePreference">languagePreference</MenuItem>
                        <MenuItem value="languagePreferenceVerbose">languagePreferenceVerbose</MenuItem>
                        <MenuItem value="lastName">lastName</MenuItem>
                        <MenuItem value="licenseNumber">licenseNumber</MenuItem>
                        <MenuItem value="logoUrl">logoUrl</MenuItem>
                        <MenuItem value="nationality">nationality</MenuItem>
                        <MenuItem value="paymentAmount">paymentAmount</MenuItem>
                        <MenuItem value="paymentOutstanding">paymentOutstanding</MenuItem>
                        <MenuItem value="paymentPaid">paymentPaid</MenuItem>
                        <MenuItem value="paymentRequired">paymentRequired</MenuItem>
                        <MenuItem value="phoneCountryCode">phoneCountryCode</MenuItem>
                        <MenuItem value="phoneNumber">phoneNumber</MenuItem>
                        <MenuItem value="raceDistance">raceDistance</MenuItem>
                        <MenuItem value="registrationNumber">registrationNumber</MenuItem>
                        <MenuItem value="representing">representing</MenuItem>
                        <MenuItem value="status">status</MenuItem>
                        <MenuItem value="travelRequired">travelRequired</MenuItem>
                        <MenuItem value="unsubscribeUrl">unsubscribeUrl</MenuItem>
                        <MenuItem value="updatedAt">updatedAt</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Editor
                    height="350px"
                    defaultLanguage="handlebars"
                    value={current.bodyTemplate}
                    onChange={handleBodyChange}
                    onMount={handleEditorDidMount}
                    options={{ minimap: { enabled: false }, wordWrap: 'on' }}
                  />
                </Paper>

                {/* Live Preview Panel */}
                <Paper elevation={2} sx={{ flex: 1, minWidth: 300, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Live Preview</Typography>
                    <Button size="small" onClick={updateLivePreview}>Refresh</Button>
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  {previewError ? (
                    <Typography color="error" variant="body2">{previewError}</Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Subject:</strong> {livePreviewSubject || '(empty)'}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Box 
                        sx={{ 
                          maxHeight: 350, 
                          overflow: 'auto', 
                          border: '1px solid', 
                          borderColor: 'divider', 
                          borderRadius: 1, 
                          p: 1,
                          bgcolor: 'background.paper'
                        }}
                        dangerouslySetInnerHTML={{ __html: livePreviewHtml || '<em>(empty)</em>' }} 
                      />
                    </>
                  )}
                </Paper>
              </Box>

              {/* Action buttons */}
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={handlePreview}>Full Preview Dialog</Button>
                <Button variant="outlined" onClick={handleFormat}>Format</Button>
                <Button variant="contained" onClick={handleSave}>Save</Button>
                <Button variant="outlined" onClick={() => { setRenamedType(current.type); setOperationError(''); setRenameDialogOpen(true); }}>Rename</Button>
                <Button variant="outlined" onClick={() => setHelpDialogOpen(true)}>Help</Button>
                <Button variant="outlined" color="error" onClick={handleDelete} disabled={loading}>Delete</Button>
                <Button variant="contained" color="secondary" onClick={() => { setTestRecipient('test@krultra.no'); setTestDialogOpen(true); }}>Test</Button>
              </Box>
            </Box>
          )}
          <Dialog open={debugLogsOpen} onClose={() => setDebugLogsOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Debug Logs</DialogTitle>
            <DialogContent>
              <pre style={{ fontSize: '12px', maxHeight: '400px', overflow: 'auto' }}>
                {debugLogs.map(log => JSON.stringify(log, null, 2)).join('\n\n')}
              </pre>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDebugLogsOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Full Preview</DialogTitle>
            <DialogContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Subject</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{previewSubject || '(empty)'}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  maxHeight: 500,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  bgcolor: 'background.paper'
                }}
                dangerouslySetInnerHTML={{ __html: previewHtml || '<em>(empty)</em>' }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Metadata Dialog */}
          <Dialog open={metadataDialogOpen} onClose={() => setMetadataDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Template Metadata</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editStatus}
                    label="Status"
                    onChange={(e) => setEditStatus(e.target.value as TemplateStatus)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Organizer"
                  value={editOrganizer}
                  onChange={(e) => setEditOrganizer(e.target.value)}
                  placeholder="e.g., KrUltra, Meråker Mountain Challenge"
                />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Event Tags</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {editEventTags.map((tag) => (
                      <Button
                        key={tag}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleRemoveEventTag(tag)}
                        sx={{ textTransform: 'none' }}
                      >
                        {tag} ×
                      </Button>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      label="Add event tag"
                      value={newEventTag}
                      onChange={(e) => setNewEventTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddEventTag()}
                      placeholder="e.g., KUTC, MO"
                    />
                    <Button size="small" variant="outlined" onClick={handleAddEventTag}>
                      Add
                    </Button>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMetadataDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveMetadata}>Save Metadata</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add New Template</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {operationError && <Typography color="error">{operationError}</Typography>}
                <TextField
                  autoFocus
                  label="Template Type"
                  value={newType}
                  onChange={e => setNewType(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newType.trim()) void addNewTemplate();
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Empty variants will be created for {EMAIL_TEMPLATE_LOCALES.map(locale =>
                    `${locale.label} (${locale.code})`
                  ).join(' and ')}. Open each locale afterwards to add its subject and content.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNewDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={addNewTemplate} disabled={!newType.trim() || loading}>
                Create
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Rename Template</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {operationError && <Typography color="error">{operationError}</Typography>}
                <Typography variant="caption" color="warning.main">
                  References to the old type in application code or saved workflows are not changed.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All locale variants named &quot;{current?.type}&quot; will be renamed together.
                </Typography>
                <TextField
                  autoFocus
                  label="New Template Type"
                  value={renamedType}
                  onChange={e => setRenamedType(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && renamedType.trim()) void handleRename();
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleRename}
                disabled={!renamedType.trim() || renamedType.trim() === current?.type || loading}
              >
                Rename All Locales
              </Button>
            </DialogActions>
          </Dialog>

          {/* Test send dialog */}
          <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  label="Recipient Email"
                  type="email"
                  fullWidth
                  value={testRecipient}
                  onChange={e => setTestRecipient(e.target.value)}
                  placeholder="you@example.com"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTestDialogOpen(false)} autoFocus>Cancel</Button>
              <Button variant="contained" onClick={handleTestSend} disabled={!testRecipient}>Send</Button>
            </DialogActions>
          </Dialog>
          
          {/* Boilerplate Management Dialog */}
          <Dialog open={boilerplateDialogOpen} onClose={() => setBoilerplateDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>{editingBoilerplate?.id ? 'Edit Boilerplate' : 'Add Boilerplate'}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  label="Tag"
                  fullWidth
                  value={editingBoilerplate?.tag || ''}
                  onChange={e => setEditingBoilerplate(editingBoilerplate ? {...editingBoilerplate, tag: e.target.value} : null)}
                  placeholder="boilerplate-tag"
                />
                <TextField
                  label="Description"
                  fullWidth
                  value={editingBoilerplate?.description || ''}
                  onChange={e => setEditingBoilerplate(editingBoilerplate ? {...editingBoilerplate, description: e.target.value} : null)}
                  placeholder="Description of what this boilerplate does"
                />
                <TextField
                  label="Template"
                  fullWidth
                  multiline
                  rows={8}
                  value={editingBoilerplate?.template || ''}
                  onChange={e => setEditingBoilerplate(editingBoilerplate ? {...editingBoilerplate, template: e.target.value} : null)}
                  placeholder="HTML content for the boilerplate"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBoilerplateDialogOpen(false)} autoFocus>Cancel</Button>
              <Button variant="contained" onClick={handleSaveBoilerplate} disabled={!editingBoilerplate?.tag || !editingBoilerplate?.template}>
                Save
              </Button>
            </DialogActions>
          </Dialog>
          
          {/* Boilerplate Insert Mode Dialog */}
          <Dialog open={insertModeDialogOpen} onClose={() => setInsertModeDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Insert Boilerplate</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Choose how to insert the boilerplate "{boilerplates.find(b => b.id === selectedBoilerplate)?.tag}":
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleInsertAsHandlebar}
                    sx={{ flex: 1, py: 2, display: 'flex', flexDirection: 'column' }}
                  >
                    <Typography variant="button">Insert as Handlebar</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {`{{>${boilerplates.find(b => b.id === selectedBoilerplate)?.tag || 'name'}}}`}
                    </Typography>
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={handleInsertAsContent}
                    sx={{ flex: 1, py: 2, display: 'flex', flexDirection: 'column' }}
                  >
                    <Typography variant="button">Insert Content</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Actual HTML
                    </Typography>
                  </Button>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>Handlebar:</strong> Updates automatically when boilerplate changes. Recommended for shared content like headers/footers.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Content:</strong> Static copy that won't auto-update. Use when you need to customize the content.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setInsertModeDialogOpen(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>
          
          {/* Debug Logs Dialog */}
          <Dialog open={debugLogsOpen} onClose={() => setDebugLogsOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Email Debug Logs</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Button variant="outlined" color="error" onClick={handleClearDebugLogs}>
                  Clear Logs
                </Button>
              </Box>
              <Box sx={{ maxHeight: 400, overflow: 'auto', backgroundColor: '#f5f5f5', padding: 2, fontFamily: 'monospace', fontSize: '12px' }}>
                {debugLogs.length === 0 ? (
                  <Typography>No debug logs available</Typography>
                ) : (
                  debugLogs.map((log, index) => (
                    <Box key={index} sx={{ mb: 1, pb: 1, borderBottom: '1px solid #ddd' }}>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>
                        {log.timestamp}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#333' }}>
                        {log.message}
                      </Typography>
                      {log.data && (
                        <Typography variant="body2" component="pre" sx={{ color: '#555', fontSize: '11px', marginTop: 0.5 }}>
                          {JSON.stringify(log.data, null, 2)}
                        </Typography>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDebugLogsOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Help Dialog */}
        <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Email Template Editor - User Guide</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Variables Section */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>📝 Template Variables</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Variables are inserted using double curly braces: <Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5', px: 1, py: 0.5, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace' }}>{'{{variableName}}'}</Box>
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Select a variable from the dropdown to insert it immediately at your cursor position.
                </Typography>
                
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Available Variables:</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1, mb: 2 }}>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{firstName}}'}</Box> - First name</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{lastName}}'}</Box> - Last name</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{email}}'}</Box> - Email address</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{eventName}}'}</Box> - Event name</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{eventShortName}}'}</Box> - Event short name</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{eventEdition}}'}</Box> - Event year</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{appBaseUrl}}'}</Box> - App base URL</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{registrationNumber}}'}</Box> - Reg. number</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{raceDistance}}'}</Box> - Race distance</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{status}}'}</Box> - Registration status</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{paymentAmount}}'}</Box> - Total amount</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{paymentPaid}}'}</Box> - Amount paid</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{paymentOutstanding}}'}</Box> - Outstanding</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{hasYearLicense}}'}</Box> - Has license (true/false)</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{licenseNumber}}'}</Box> - License number</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{today}}'}</Box> - Current date</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{krultraStationaryImageUrl}}'}</Box> - Header image</Typography>
                  <Typography variant="caption"><Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{unsubscribeUrl}}'}</Box> - Unsubscribe link URL</Typography>
                  
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Conditionals:</Typography>
                  <Box sx={{ 
    bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', 
    p: 2, 
    borderRadius: 1, 
    mb: 2, 
    fontFamily: 'monospace', 
    fontSize: '0.875rem',
    color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000'
  }}>
                    {'{{#if hasYearLicense}}'}<br />
                    &nbsp;&nbsp;License: {'{{licenseNumber}}'}<br />
                    {'{{else}}'}<br />
                    &nbsp;&nbsp;No license on file<br />
                    {'{{/if}}'}
                  </Box>
                  
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Loops:</Typography>
                  <Box sx={{ 
    bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', 
    p: 2, 
    borderRadius: 1, 
    mb: 2, 
    fontFamily: 'monospace', 
    fontSize: '0.875rem',
    color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000'
  }}>
                    {'{{#each payments}}'}<br />
                    &nbsp;&nbsp;Payment: {'{{this.amount}}'} on {'{{formatDate this.date}}'}<br />
                    {'{{/each}}'}
                  </Box>
                  
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Custom Helpers:</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.875rem' }}>{'{{formatDate date "DD MMM YYYY"}}'}</Box> - Format dates<br/>
                    <Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.875rem' }}>{'{{formatDateTime date}}'}</Box> - Format date/time<br/>
                    <Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.875rem' }}>{'{{upper text}}'}</Box> / <Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.875rem' }}>{'{{lower text}}'}</Box> - Change case
                  </Typography>
                </Box>

                {/* Boilerplates Section */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>🧩 Boilerplates</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Boilerplates are reusable template components. Insert them using:
                  </Typography>
                  <Box sx={{ 
    bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', 
    p: 2, 
    borderRadius: 1, 
    mb: 2, 
    fontFamily: 'monospace', 
    fontSize: '0.875rem',
    color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000'
  }}>
                    {'{{>boilerplateName}}'}
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Insert Options:</strong><br/>
                    • <strong>Handlebar</strong>: Inserts <Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.875rem' }}>{'{{>name}}'}</Box> - resolves when email is sent<br/>
                    • <strong>Content</strong>: Inserts the actual boilerplate content immediately
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Features:</strong><br/>
                    • Nested boilerplates (up to 3 levels deep)<br/>
                    • Circular reference detection<br/>
                    • Missing boilerplate warnings
                  </Typography>
                </Box>

                {/* Metadata Section */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>📊 Template Metadata</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Metadata fields are now directly editable in the template editor:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • <strong>Status:</strong> Active, Draft, Inactive, or Deprecated<br/>
                    • <strong>Organizer:</strong> Who organizes this template<br/>
                    • <strong>Event Tags:</strong> Tags for filtering (e.g., KUTC, MO)
                  </Typography>
                </Box>

                {/* Tips Section */}
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>💡 Pro Tips</Typography>
                  <Typography variant="body2" component="div">
                    <ul>
                      <li>Use the <strong>Preview</strong> panel to see your template in real-time</li>
                      <li>Test emails use sample data - check actual emails with real registrations</li>
                      <li>Use <strong>Format</strong> button to clean up HTML structure</li>
                      <li>Variables are inserted immediately when selected from dropdown</li>
                      <li>License fields: <Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.875rem' }}>{'{{hasYearLicense}}'}</Box> (boolean) and <Box component="code" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200', px: 0.5, py: 0.25, borderRadius: 0.5, color: theme => theme.palette.mode === 'dark' ? '#fff' : '#000', fontFamily: 'monospace', fontSize: '0.875rem' }}>{'{{licenseNumber}}'}</Box></li>
                    </ul>
                  </Typography>
                </Box>
              </Box>
            </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default TemplatesPanel;
